import { Menu, Transition } from '@headlessui/react';
import {
  Moon,
  Sun,
  LogOut,
  Settings,
  Activity as ActivityIcon,
  Box,
  Layers,
} from 'react-feather';
import clsx from 'clsx';
import { useAuth } from 'lib/auth';
import React, { useEffect, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import AvatarImg from 'assets/avatar.png';
import { imageToBlob } from 'utils/ImageToBlob';
import { useTheme } from 'hooks/useTheme';

export const UserNavigation = () => {
  const { user, logout } = useAuth();
  const { theme, background } = useTheme();

  const [userPhoto, setUserPhoto] = useState('');
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme') === 'dark'
  );

  useEffect(() => {
    let objectUrl;
    if (user?.photo) {
      imageToBlob(user.photo).then((localURL) => {
        if (localURL) {
          setUserPhoto(localURL);
          objectUrl = localURL;
        }
      });
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [user?.photo]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const version = 'v1.4.0';

  return (
    <Menu as="div" className="ml-3 relative z-50">
      {({ open }) => (
        <>
          <Menu.Button>
            <div className="w-12 h-12 rounded-full overflow-hidden shadow-md border-2 border-white/20">
              <img
                src={userPhoto || AvatarImg}
                alt="User Avatar"
                className="w-full h-full object-cover"
                style={{ display: photoLoaded ? 'block' : 'none' }}
                onLoad={() => setPhotoLoaded(true)}
                onError={() => setPhotoLoaded(true)}
              />
            </div>
          </Menu.Button>

          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              static
              className={clsx(
                'origin-top-right absolute right-0 mt-2 w-72 rounded-2xl p-4 backdrop-blur-xl shadow-xl ring-1 ring-white/20 border border-white/10 text-white overflow-hidden',
                theme
              )}
            >
              {/* Optional background overlay */}
              {background && (
                <div className="absolute inset-0 pointer-events-none opacity-10 z-0">
                  <img
                    src={background}
                    alt="Decorative background"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* User Profile */}
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-20 h-20 rounded-full overflow-hidden mb-3 border-2 border-white/20 shadow">
                  <img
                    src={userPhoto || AvatarImg}
                    alt="User Avatar"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col items-center space-y-0.5 max-w-full">
                  <p className="text-md font-semibold text-white max-w-[85%] truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-white/70 max-w-[85%] truncate">
                    {user?.email}
                  </p>
                </div>

                <p className="text-sm text-white/60">{user?.phoneNumber}</p>
                <p className="text-xs my-3 text-white/50 italic">
                  {user?.accountType || 'Role not specified'}
                </p>
              </div>

              <hr className="my-3 border-t border-white/10" />

              {/* Navigation Links */}
              <div className="relative z-10 text-left">
                {[
                  { to: './activity', icon: ActivityIcon, label: 'Activity' },
                  { to: './integrations', icon: Layers, label: 'Integrations' },
                  { to: './widgets', icon: Box, label: 'Widgets' },
                  {
                    to: './account/settings',
                    icon: Settings,
                    label: 'Account Settings',
                  },
                ].map(({ to, icon: Icon, label }) => (
                  <Menu.Item key={to}>
                    {({ active }) => (
                      <Link
                        to={to}
                        className={clsx(
                          'flex items-center px-3 py-2 rounded-md text-sm transition-all',
                          active
                            ? 'bg-white/10 text-white'
                            : 'text-white/80 hover:bg-white/10'
                        )}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {label}
                      </Link>
                    )}
                  </Menu.Item>
                ))}

                <hr className="my-3 border-t border-white/10" />

                {/* Logout */}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={logout}
                      className={clsx(
                        'w-full flex items-center px-3 py-2 rounded-md text-sm',
                        active
                          ? 'bg-white/10 text-white'
                          : 'text-white/80 hover:bg-white/10'
                      )}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </button>
                  )}
                </Menu.Item>

                {/* Footer Info */}
                <div className="mt-4 text-xs text-white/50 text-center">
                  <p>{version}</p>
                  <a href="#" className="underline hover:text-white/80">
                    Terms and Conditions
                  </a>
                </div>
              </div>
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};
